// src/components/matchInput/AssignInfractionStep.js
import React from "react";
import SelectBox from "../../central/SelectBox";

const AssignInfractionStep = ({
  players = [],
  infractionsSelected = [],
  infractionAssignments = {},
  setInfractionAssignments,
  cleanSheet,
  appearanceData,
  leagueType = "",
}) => {
  // Fixed list of infractions to render
  const fixedInfractions = [
    "GK 3+ Save Bonus",
    "Penalty Save",
    "Yellow Card",
    "Penalty Miss",
    "Own Goal",
  ];

  if (!infractionsSelected || infractionsSelected.length === 0) {
    infractionsSelected = fixedInfractions;
  }

  // Helper to determine if a player appeared, supporting various formats
  const didAppear = (appearance) => {
    if (!appearance) return false;
    if (typeof appearance === "boolean") return appearance;
    if (typeof appearance === "object") return !!appearance.appeared;
    return false;
  };
  // Support both object map { [id]: { appeared } } and array [{ id, appeared }]
  const appearedIds = new Set(
    Array.isArray(appearanceData)
      ? appearanceData.filter(a => a && didAppear(a.appeared) && a.id).map(a => String(a.id))
      : Object.keys(appearanceData || {}).filter(id => didAppear(appearanceData[id]))
  );
  const appearedPlayers = players.filter(
    (p) => didAppear(appearanceData?.[String(p.id)]) || appearedIds.has(String(p.id))
  );

  // GK-only filtering: must have appeared AND be explicitly marked as GK in appearanceData
  const eligibleGKPlayers = players.filter(
    p => appearanceData?.[String(p.id)]?.appeared && appearanceData?.[String(p.id)]?.isGK
  );

  React.useEffect(() => {
    setInfractionAssignments(prev => {
      const updated = { ...prev };
      const gkInfractions = ["GK 3+ Save Bonus", "Penalty Save"];
      gkInfractions.forEach(inf => {
        if (infractionsSelected.includes(inf) && eligibleGKPlayers.length > 0 && !updated[inf]) {
          updated[inf] = eligibleGKPlayers[0].id;
        }
      });
      return updated;
    });
  }, [infractionsSelected, eligibleGKPlayers]);

  // Debugging logs
  console.log("AssignInfractionStep players:", players);
  console.log("AssignInfractionStep appearanceData:", appearanceData);
  console.log("AssignInfractionStep appearedIds:", Array.from(appearedIds));
  console.log("AssignInfractionStep appearedPlayers:", appearedPlayers);
  console.log("AssignInfractionStep eligibleGKPlayers:", eligibleGKPlayers);

  // Normalize to always save arrays of player IDs for scoring consistency (see calculatePointsFromMatches.js)
  const toggleMultiAssignment = (infractionName, playerId) => {
    setInfractionAssignments((prev) => {
      const current = Array.isArray(prev?.[infractionName]) ? prev[infractionName] : [];
      const exists = current.includes(playerId);
      const next = exists ? current.filter((id) => id !== playerId) : [...current, playerId];
      return { ...prev, [infractionName]: next };
    });
  };

  const setSingleAssignment = (infractionName, playerId) => {
    setInfractionAssignments((prev) => ({ ...prev, [infractionName]: [playerId] }));
  };

  // Define which infractions are multi-assign (checkboxes)
  const multiAssignInfractions = ["Yellow Card", "Penalty Miss", "Own Goal"];

  return (
    <div>
      {/* 🔹 Single global step header comes from MatchInput, so we only keep a section heading */}
      <h4>Assign Infractions</h4>
      <div
        style={{
          fontStyle: "italic",
          marginBottom: 14,
          fontSize: "0.9em",
        }}
      >
        Tap at least one player
      </div>

      {fixedInfractions.map((infraction) => {
        if (!infractionsSelected.includes(infraction)) {
          return null;
        }
        const isMulti = multiAssignInfractions.includes(infraction);
        const isOwnGoal = infraction === "Own Goal";
        const isGKOnly = infraction === "GK 3+ Save Bonus" || infraction === "Penalty Save";
        const disabledGlobal = isOwnGoal && cleanSheet;

        const playersToRender = isGKOnly ? eligibleGKPlayers : appearedPlayers;

        return (
          <div key={infraction} style={{ marginBottom: "1rem" }}>
            <h5>{infraction}</h5>
            {playersToRender.length === 0 ? (
              <p>No eligible players</p>
            ) : (
              playersToRender.map((p) => {
                // Show glove icon if player is GK by any means (for label only)
                const isGK =
                  p.isGK ||
                  p.role === "GK" ||
                  p.position === "GK" ||
                  (appearanceData?.[String(p.id)] &&
                    (appearanceData[String(p.id)].isGK ||
                      appearanceData[String(p.id)].role === "GK" ||
                      appearanceData[String(p.id)].position === "GK"));
                const disabled = disabledGlobal;
                return (
                  <SelectBox
                    key={p.id}
                    selected={(infractionAssignments[infraction] || []).includes(p.id)}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      if (isMulti) {
                        toggleMultiAssignment(infraction, p.id);
                      } else {
                        setSingleAssignment(infraction, p.id);
                      }
                    }}
                  >
                    {p.name}
                    {isGK ? " 🧤" : ""} ({p.position})
                  </SelectBox>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssignInfractionStep;